// src/components/reports/ReportList.jsx
import React, { useState, useMemo, useId, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useFetchReports }  from "../../hooks/queries/useFetchReports";
import { useConfirmReport } from "../../hooks/mutations/useConfirmReport";
import { useDeleteReport } from "../../hooks/mutations/useDeleteReport";
import PointMap            from "../ui/PointMap";
import { Spinner } from "../ui/Spinner";
import { Pagination } from "../ui/Pagination";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useReportSignedImageUrl } from "../../hooks/queries/useFetchReportSignedImageUrl";

function ChevronDownIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M7 10L12 15L17 10" fill="currentColor" />
    </svg>
  );
}

function ReviewTileIcon() {
  return (
    <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M1.80775 12.246C1.30258 12.246 0.875 12.071 0.525 11.721C0.175 11.371 0 10.9435 0 10.4385V4.496C0 4.3345 0.0336667 4.17975 0.101 4.03175C0.168333 3.88358 0.259 3.7525 0.373 3.6385L3.648 0.363501C3.7725 0.239168 3.90875 0.147501 4.05675 0.088501C4.20475 0.029501 4.35125 0 4.49625 0C4.85258 0 5.15867 0.134584 5.4145 0.403751C5.67017 0.673084 5.76275 1.018 5.69225 1.4385L5.24225 3.74625H10.3077C10.6487 3.74625 10.9327 3.85967 11.1595 4.0865C11.3865 4.3135 11.5 4.5975 11.5 4.9385V5.746C11.5 5.846 11.4917 5.93867 11.475 6.024C11.4583 6.10917 11.4333 6.19342 11.4 6.27675L9.16925 11.4808C9.06542 11.7192 8.90833 11.9064 8.698 12.0423C8.48783 12.1781 8.25517 12.246 8 12.246H1.80775ZM13.1923 17.4768C12.8513 17.4768 12.5673 17.3633 12.3405 17.1365C12.1135 16.9095 12 16.6255 12 16.2845V15.477C12 15.377 12.0083 15.2843 12.025 15.199C12.0417 15.1138 12.0667 15.0296 12.1 14.9463L14.3307 9.74225C14.4449 9.50375 14.6046 9.31658 14.8098 9.18075C15.0148 9.04492 15.2448 8.977 15.5 8.977H21.6923C22.1974 8.977 22.625 9.152 22.975 9.502C23.325 9.852 23.5 10.2795 23.5 10.7845V16.727C23.5 16.8885 23.4689 17.0407 23.4068 17.1835C23.3446 17.3265 23.2513 17.4602 23.127 17.5845L19.852 20.8595C19.7275 20.9838 19.5913 21.0755 19.4433 21.1345C19.2952 21.1935 19.1488 21.223 19.0038 21.223C18.6474 21.223 18.3413 21.0884 18.0855 20.8193C17.8298 20.5499 17.7373 20.205 17.8078 19.7845L18.2577 17.4768H13.1923Z"
        fill="currentColor"
      />
    </svg>
  );
}

function TrashTileIcon() {
  const maskId = useId();
  return (
    <svg width="39" height="40" viewBox="0 0 39 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <mask id={maskId} style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="7" y="8" width="25" height="24">
        <rect x="7.5" y="8" width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path
          d="M14.8077 28.5017C14.3106 28.5017 13.885 28.3247 13.531 27.9707C13.177 27.6167 13 27.1911 13 26.6939V14.0017H12.75C12.5375 14.0017 12.3594 13.9298 12.2158 13.7859C12.0719 13.6421 12 13.4639 12 13.2514C12 13.0388 12.0719 12.8607 12.2158 12.7172C12.3594 12.5735 12.5375 12.5017 12.75 12.5017H16.5C16.5 12.2569 16.5863 12.0482 16.7588 11.8757C16.9311 11.7034 17.1397 11.6172 17.3845 11.6172H21.6155C21.8603 11.6172 22.0689 11.7034 22.2413 11.8757C22.4138 12.0482 22.5 12.2569 22.5 12.5017H26.25C26.4625 12.5017 26.6406 12.5736 26.7843 12.7174C26.9281 12.8613 27 13.0394 27 13.2519C27 13.4646 26.9281 13.6427 26.7843 13.7862C26.6406 13.9299 26.4625 14.0017 26.25 14.0017H26V26.6939C26 27.1911 25.823 27.6167 25.469 27.9707C25.115 28.3247 24.6894 28.5017 24.1923 28.5017H14.8077ZM17.6543 25.0017C17.8668 25.0017 18.0448 24.9299 18.1885 24.7862C18.332 24.6424 18.4037 24.4642 18.4037 24.2517V16.7517C18.4037 16.5392 18.3318 16.361 18.188 16.2172C18.0443 16.0735 17.8662 16.0017 17.6535 16.0017C17.441 16.0017 17.2629 16.0735 17.1192 16.2172C16.9757 16.361 16.904 16.5392 16.904 16.7517V24.2517C16.904 24.4642 16.9758 24.6424 17.1195 24.7862C17.2633 24.9299 17.4416 25.0017 17.6543 25.0017ZM21.3465 25.0017C21.559 25.0017 21.7371 24.9299 21.8807 24.7862C22.0243 24.6424 22.096 24.4642 22.096 24.2517V16.7517C22.096 16.5392 22.0242 16.361 21.8805 16.2172C21.7367 16.0735 21.5584 16.0017 21.3458 16.0017C21.1333 16.0017 20.9552 16.0735 20.8115 16.2172C20.668 16.361 20.5962 16.5392 20.5962 16.7517V24.2517C20.5962 24.4642 20.6682 24.6424 20.812 24.7862C20.9557 24.9299 21.1338 25.0017 21.3465 25.0017Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}

function EmptyReportsIcon() {
  return (
    <svg width="82" height="82" viewBox="0 0 82 82" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-80">
      <g opacity="0.8">
        <path fillRule="evenodd" clipRule="evenodd" d="M54.1351 40.6117L40.5449 54.2006C40.5449 54.2006 45.3778 52.6913 52.3235 55.1065C53.492 55.5126 55.4471 53.5574 55.041 52.3889C52.6258 45.4433 54.1351 40.6117 54.1351 40.6117Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M17.7636 17.8298C26.0149 9.57984 39.4104 9.57984 47.6616 17.8298C55.9116 26.0811 55.9116 39.4765 47.6616 47.7278C39.4104 55.9777 26.0149 55.9777 17.7636 47.7278C9.51368 39.4765 9.51368 26.0811 17.7636 17.8298Z" fill="#E9F3FC"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M44.9279 15.521C45.8863 16.2013 46.8011 16.9714 47.6608 17.8298C55.9108 26.0811 55.9108 39.4765 47.6608 47.7278C39.4096 55.9777 26.0141 55.9777 17.7628 47.7278C17.2773 47.2409 16.8198 46.7374 16.3906 46.2185C24.3882 48.8168 33.5286 46.9411 39.8785 40.5912C46.6512 33.8185 48.3348 23.8709 44.9279 15.521Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M15.9516 16.0182C25.2023 6.76754 40.2224 6.76754 49.473 16.0182C58.7236 25.2688 58.7236 40.2889 49.473 49.5395C40.2224 58.7901 25.2023 58.7901 15.9516 49.5395C6.70102 40.2889 6.70102 25.2688 15.9516 16.0182ZM18.6692 18.7357C26.4195 10.9854 39.0052 10.9854 46.7555 18.7357C54.5057 26.486 54.5057 39.0717 46.7555 46.822C39.0052 54.5723 26.4195 54.5723 18.6692 46.822C10.9189 39.0717 10.9189 26.486 18.6692 18.7357Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M11.4763 43.319C7.07904 34.454 8.57042 23.3994 15.9517 16.0181C22.8833 9.08655 33.0538 7.34918 41.6113 10.806C41.6728 11.5914 41.7048 12.3871 41.7048 13.1904C41.7048 13.8105 41.6856 14.4268 41.6485 15.0392C34.2031 11.2903 24.8833 12.5229 18.6692 18.7356C12.0823 25.3225 11.0932 35.4021 15.7044 43.0384C14.354 43.2229 12.9754 43.319 11.5762 43.319H11.4763Z" fill="#B9CAE3"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L78.0109 69.9242C78.5106 70.4239 77.0897 72.6571 74.8398 74.907C72.59 77.1569 70.3568 78.5778 69.8571 78.0781L51.2841 59.5051Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L76.2479 68.1612C76.7476 68.6609 75.3267 70.8941 73.0768 73.144C70.827 75.3939 68.5938 76.8148 68.0941 76.3151L51.2841 59.5051Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M29.9982 28.7448C29.9687 27.5238 30.4274 26.632 31.1347 26.1093C32.2468 25.2893 33.9086 25.3239 34.8106 26.3399C35.1975 26.7768 35.4217 27.3944 35.4281 28.1759C35.4435 30.0504 34.5646 31.1459 33.7036 32.2798C32.5453 33.8083 31.3883 35.3791 31.2448 38.0018C31.2064 38.7091 31.7497 39.3125 32.4556 39.351C33.1616 39.3894 33.7664 38.8474 33.8048 38.1415C33.9124 36.1491 34.8657 34.9883 35.7459 33.8275C36.9246 32.2734 38.0124 30.723 37.9906 28.1554C37.9778 26.6461 37.4743 25.4814 36.726 24.6384C34.9784 22.6704 31.7689 22.4577 29.6138 24.0464C28.3223 24.9997 27.3832 26.5782 27.4357 28.805C27.4523 29.5123 28.0404 30.0722 28.7477 30.0555C29.4537 30.0389 30.0148 29.452 29.9982 28.7448Z" fill="#8DA5CF"/>
        <path d="M32.406 44.1095C33.2764 44.1095 33.982 43.4039 33.982 42.5336C33.982 41.6632 33.2764 40.9576 32.406 40.9576C31.5356 40.9576 30.8301 41.6632 30.8301 42.5336C30.8301 43.4039 31.5356 44.1095 32.406 44.1095Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M55.4638 10.8867L59.3076 4.48048C59.6715 3.87445 59.4754 3.08648 58.8681 2.72261C58.2621 2.36001 57.4741 2.55604 57.1103 3.16207L53.2665 9.56833C52.9026 10.1756 53.0999 10.9636 53.706 11.3262C54.3133 11.6901 55.1 11.494 55.4638 10.8867Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M61.5248 15.1084L75.6185 4.85845C76.1899 4.44204 76.3168 3.63998 75.9017 3.06854C75.4853 2.49582 74.6832 2.37026 74.1118 2.78539L60.018 13.0354C59.4453 13.4518 59.3197 14.2539 59.7349 14.8253C60.1513 15.398 60.9533 15.5236 61.5248 15.1084Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M63.6442 23.0022L73.8942 20.4397C74.5809 20.268 74.9986 19.5723 74.8269 18.8856C74.6552 18.2001 73.9595 17.7824 73.2728 17.9541L63.0228 20.5166C62.3373 20.6883 61.9196 21.384 62.0913 22.0708C62.263 22.7562 62.9587 23.1739 63.6442 23.0022Z" fill="#8DA5CF"/>
      </g>
    </svg>
  );
}

/* ---------- Calendar helpers ---------- */

const calendarWeekdayLabels = ["M", "T", "W", "T", "F", "S", "S"];
const toIsoDate = (date) => date.toISOString().split("T")[0];

const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isWithinRange = (day, start, end) => {
  if (!day || !start || !end) return false;
  return day > start && day < end;
};

const buildCalendarMatrix = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  const start = new Date(firstOfMonth);
  const startOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - startOffset);

  const end = new Date(lastOfMonth);
  const endOffset = 6 - ((end.getDay() + 6) % 7);
  end.setDate(end.getDate() + endOffset);

  const weeks = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      const date = new Date(cursor);
      const inMonth = date.getMonth() === month;
      week.push({ date, label: String(date.getDate()), inMonth });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

/* ---- UI helpers ---- */

function reviewPill(isReviewed) {
  if (isReviewed) {
    return { bg: "#C6D3FF", border: "#C6D3FF", text: "#0C3188", label: "Reviewed", w: "w-[70px]" };
  }
  return { bg: "#FFE2C6", border: "#FFE2C6", text: "#885B0C", label: "Pending", w: "w-[62px]" };
}

export default function ReportList() {
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filters (review status + dates)
  // reviewFilter: "" | "pending" | "reviewed"
  const [reviewFilter, setReviewFilter] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data, isLoading, isError, error } = useFetchReports(page, pageSize, {
    reviewFilter,
    startDate,
    endDate,
  });
  const reports = useMemo(() => {
    return Array.isArray(data) ? data : (data?.results ?? []);
  }, [data]);
  
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const confirmMutation = useConfirmReport();
  const deleteMutation = useDeleteReport();

  const [reviewReport, setReviewReport] = useState(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const openReview = (report) => {
    setDidRetryImg(false);
    setReviewReport(report);
    setIsReviewOpen(true);
  };

  const closeReview = () => {
    setIsReviewOpen(false);
    setReviewReport(null);
    setDidRetryImg(false);
  };

  const hasReviewImage = !!reviewReport?.image;

  const isReviewed =
    reviewReport?.stakeholder_confirmed !== null &&
    reviewReport?.stakeholder_confirmed !== undefined;

  const isConfirmed = reviewReport?.stakeholder_confirmed === true;

  const {
    data: signedImageUrl,
    isLoading: isSignedImgLoading,
    isError: isSignedImgError,
    refetch: refetchSignedImg,
  } = useReportSignedImageUrl(reviewReport?.id, isReviewOpen && hasReviewImage);

  // image url
  const makeFullUrl = (path) => {
    if (!path) return "";
    const base = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
    if (/^https?:\/\//.test(path)) {
      try {
        const url = new URL(path);
        const apiUrl = new URL(base);
        if (
          url.pathname.startsWith("/media/") &&
          ["localhost", "127.0.0.1"].includes(url.hostname) &&
          !url.port
        ) {
          url.protocol = apiUrl.protocol;
          url.hostname = apiUrl.hostname;
          url.port = apiUrl.port;
          return url.toString();
        }
      } catch (_) {
        return path;
      }
      return path;
    }
    const relative = path.startsWith("/") ? path : `/${path}`;
    return `${base}${relative}`;
  };

  const reviewImageUrl = makeFullUrl(signedImageUrl || reviewReport?.image);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, reportId: null });

  // Filter state
  // const [statusFilter, setStatusFilter] = useState("");
  // const [solvedFilter, setSolvedFilter] = useState("");

  // Calendar overlay state
  const today = new Date();
  const [calendarOpenFor, setCalendarOpenFor] = useState(null); // "start" | "end" | null
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
  const [calendarYear, setCalendarYear] = useState(today.getFullYear());
  const calendarDropdownRef = useRef(null);
  const filtersBarRef = useRef(null);
  const startCalendarTriggerRef = useRef(null);
  const endCalendarTriggerRef = useRef(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const [didRetryImg, setDidRetryImg] = useState(false);

  const reviewDropdownRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [reviewFilter, startDate, endDate]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const reviewOutside = reviewDropdownRef.current && !reviewDropdownRef.current.contains(e.target);
      if (reviewOutside) setReviewOpen(false);
    };
    document.addEventListener("click", onDocMouseDown);
    return () => document.removeEventListener("click", onDocMouseDown);
  }, []);

  const calendarMatrix = useMemo(() => buildCalendarMatrix(calendarYear, calendarMonth), [calendarMonth, calendarYear]);
  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);
  const activeMonthLabel = new Date(calendarYear, calendarMonth).toLocaleString("en-US", { month: "long" });

  const formatCalendarInput = (value) => {
    if (!value) return "";
    const parsed = parseIsoDate(value);
    return parsed ? parsed.toLocaleDateString("en-GB") : "";
  };

  const handleCalendarNav = (direction) => {
    const next = new Date(calendarYear, calendarMonth + direction);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const openCalendar = (target) => {
    if (calendarOpenFor === target) {
      setCalendarOpenFor(null);
      return;
    }

    const triggerEl = target === "start" ? startCalendarTriggerRef.current : endCalendarTriggerRef.current;
    const containerEl = filtersBarRef.current;

    if (triggerEl && containerEl) {
      const triggerRect = triggerEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      const overlayWidth = 222;
      const padding = 16;
      const desiredLeft = triggerRect.left - containerRect.left;
      const maxLeft = Math.max(padding, containerRect.width - overlayWidth - padding);
      const clampedLeft = Math.max(padding, Math.min(desiredLeft, maxLeft));

      setCalendarPosition({
        top: triggerRect.bottom - containerRect.top + 8,
        left: clampedLeft,
      });
    }

    const referenceIso = target === "start" ? startDate : endDate;
    const fallbackIso = startDate || endDate;
    const referenceDate = parseIsoDate(referenceIso) || parseIsoDate(fallbackIso) || new Date();
    setCalendarYear(referenceDate.getFullYear());
    setCalendarMonth(referenceDate.getMonth());

    setCalendarOpenFor(target);
  };

  const handleDaySelect = (date) => {
    if (!calendarOpenFor) return;

    const iso = toIsoDate(date);
    if (calendarOpenFor === "start") {
      setStartDate(iso);
      if (endDate && parseIsoDate(endDate) && parseIsoDate(endDate) < date) setEndDate(iso);
    } else {
      setEndDate(iso);
      if (startDate && parseIsoDate(startDate) && parseIsoDate(startDate) > date) setStartDate(iso);
    }

    setCalendarOpenFor(null);
  };

  const clearCalendarSelection = () => {
    setStartDate("");
    setEndDate("");
    setCalendarOpenFor(null);
  };

  useEffect(() => {
    if (!calendarOpenFor) return undefined;
    const onDocClick = (event) => {
      if (
        calendarDropdownRef.current &&
        !calendarDropdownRef.current.contains(event.target) &&
        !startCalendarTriggerRef.current?.contains(event.target) &&
        !endCalendarTriggerRef.current?.contains(event.target)
      ) {
        setCalendarOpenFor(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [calendarOpenFor]);

  useEffect(() => {
    if (!isReviewOpen) return;
    const onKeyDown = (e) => e.key === "Escape" && closeReview();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isReviewOpen]);

  useEffect(() => {
    if (!isReviewOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeReview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isReviewOpen]);


  // Filter reports
  const filteredReports = useMemo(() => {
    return reports.filter((r) => {
      const isReviewed = r.stakeholder_confirmed !== null && r.stakeholder_confirmed !== undefined;

      if (reviewFilter === "pending" && isReviewed) return false;
      if (reviewFilter === "reviewed" && !isReviewed) return false;

      if (startDate) {
        const reportDate = new Date(r.reported_at);
        const filterStart = new Date(startDate);
        if (reportDate < filterStart) return false;
      }
      if (endDate) {
        const reportDate = new Date(r.reported_at);
        const filterEnd = new Date(endDate);
        filterEnd.setHours(23, 59, 59, 999);
        if (reportDate > filterEnd) return false;
      }

      return true;
    });
  }, [reports, reviewFilter, startDate, endDate]);

  const clearFilters = () => {
    setReviewFilter("");
    setStartDate("");
    setEndDate("");
    setReviewOpen(false);
    setCalendarOpenFor(null);
  };

  const handleDelete = (id) => setDeleteConfirm({ isOpen: true, reportId: id });

  const confirmDelete = () => {
  const { reportId } = deleteConfirm;

  if (reviewReport?.id === reportId) closeReview();

  deleteMutation.mutate(reportId);
  setDeleteConfirm({ isOpen: false, reportId: null });
};


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="h-12 w-12 text-camber-blue-500 mx-auto mb-4" />
          <p className="text-camber-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }
  if (isError) return <p className="text-red-600 font-medium">Error: {error.message}</p>;

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = totalCount === 0 ? 0 : (page - 1) * pageSize + filteredReports.length;

  // if (isLoading) {
  //   return (
  //     <div className="flex items-center justify-center py-12">
  //       <div className="text-center">
  //         <Spinner className="h-12 w-12 text-camber-blue-500 mx-auto mb-4" />
  //         <p className="text-camber-gray-600">Loading reports...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // if (isError) {
  //   return (
  //     <div className="bg-red-50 border border-red-200 rounded-lg p-4">
  //       <p className="text-red-700 font-medium">Error: {error.message}</p>
  //     </div>
  //   );
  // }

  // if (reports.length === 0) {
  //   return (
  //     <div className="bg-camber-gray-50 border border-camber-gray-200 rounded-lg p-8 text-center">
  //       <p className="text-camber-gray-600 italic">No reports found.</p>
  //     </div>
  //   );
  // }

  const isTrulyEmpty = reports.length === 0;
  const emptyMessage = isTrulyEmpty ? "No reports found." : "No reports match your filters.";

  return (
    <div className="w-full font-['Open_Sans']">
      {/* List Card */}
      <div className={`w-full bg-[#FDFEFF] rounded-[8px] p-[8px] ${filteredReports.length === 0 ? "min-h-[529px]" : ""}`}>
        {/* Filters bar */}
        <div
          ref={filtersBarRef}
          className="w-full min-h-[64px] bg-[#F4F7FA] rounded-[4px] px-[16px] py-[16px] flex flex-wrap items-center justify-between gap-y-[12px] relative"
        >
          <div className="flex flex-wrap items-center gap-[8px]">
            {/* Review Status dropdown */}
            <div ref={reviewDropdownRef} className="relative w-[160px] h-[32px]">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReviewOpen((v) => !v);
                }}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between"
                aria-haspopup="listbox"
                aria-expanded={reviewOpen}
              >
                <span className={`text-[12px] leading-[155%] ${reviewFilter ? "text-[#333333]" : "text-[#939393]"}`}>
                  {reviewFilter === "" ? "Review Status" : reviewFilter === "reviewed" ? "Reviewed" : "Pending"}
                </span>
                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <ChevronDownIcon />
                </span>
              </button>

              {reviewOpen && (
                <div className="absolute left-0 top-[32px] w-[160px] bg-white border border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setReviewFilter("pending");
                      setReviewOpen(false);
                    }}
                    className="w-full h-[42px] flex items-center justify-center hover:bg-[#F4F7FA]"
                    role="option"
                    aria-selected={reviewFilter === "pending"}
                  >
                    <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#FFE2C6] bg-[#FFE2C6] text-[12px] leading-[155%] text-[#885B0C]">
                      Pending
                    </span>
                  </button>

                  <div className="w-full h-[1px] bg-[#E0E2E5]" />

                  <button
                    type="button"
                    onClick={() => {
                      setReviewFilter("reviewed");
                      setReviewOpen(false);
                    }}
                    className="w-full h-[42px] flex items-center justify-center hover:bg-[#F4F7FA]"
                    role="option"
                    aria-selected={reviewFilter === "reviewed"}
                  >
                    <span className="inline-flex items-center justify-center h-[23px] px-[8px] rounded-[182px] border border-[#C6D3FF] bg-[#C6D3FF] text-[12px] leading-[155%] text-[#0C3188]">
                      Reviewed
                    </span>
                  </button>

                  <div className="w-full h-[1px] bg-[#E0E2E5]" />

                  <button
                    type="button"
                    onClick={() => {
                      setReviewFilter("");
                      setReviewOpen(false);
                    }}
                    className="w-full h-[42px] flex items-center justify-center text-[12px] leading-[155%] text-[#0B0B0B] hover:bg-[#F4F7FA]"
                    role="option"
                    aria-selected={reviewFilter === ""}
                  >
                    All
                  </button>
                </div>
              )}
            </div>

            {/* Start Date */}
            <div ref={startCalendarTriggerRef} className="relative w-[129px] h-[32px]">
              <button
                type="button"
                onClick={() => openCalendar("start")}
                aria-haspopup="true"
                aria-expanded={calendarOpenFor === "start"}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
              >
                <span className={startDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                  {startDate ? formatCalendarInput(startDate) : "Start Date"}
                </span>
                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <ChevronDownIcon />
                </span>
              </button>
            </div>

            {/* End Date */}
            <div ref={endCalendarTriggerRef} className="relative w-[123px] h-[32px]">
              <button
                type="button"
                onClick={() => openCalendar("end")}
                aria-haspopup="true"
                aria-expanded={calendarOpenFor === "end"}
                className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
              >
                <span className={endDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                  {endDate ? formatCalendarInput(endDate) : "End Date"}
                </span>
                <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                  <ChevronDownIcon />
                </span>
              </button>
            </div>

            {/* Calendar overlay */}
            {calendarOpenFor && (
              <div
                ref={calendarDropdownRef}
                className="absolute z-30 w-[222px] h-[249px] bg-white border border-[#E0E2E5] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] rounded-[4px]"
                style={{ top: calendarPosition.top, left: calendarPosition.left }}
              >
                <div className="flex flex-col items-start px-[14px] py-[10px] gap-[8px]">
                  <div className="w-full h-[22px] flex items-center justify-between">
                    <div className="text-[16px] leading-[22px] font-normal text-[#333333]">{activeMonthLabel}</div>

                    <div className="h-[16px] flex items-center justify-center gap-[4px]">
                      <button type="button" onClick={() => handleCalendarNav(-1)} className="w-[11px] h-[11px] flex items-center justify-center" aria-label="Previous month">
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.5 2.5L4.5 6L7.5 9.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>

                      <div className="w-[28px] text-center text-[12px] leading-[16px] font-normal text-[#333333]">{calendarYear}</div>

                      <button type="button" onClick={() => handleCalendarNav(1)} className="w-[11px] h-[11px] flex items-center justify-center" aria-label="Next month">
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.5 9.5L7.5 6L4.5 2.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="w-full flex flex-col items-start px-[7px]">
                  <div className="w-[208px] h-[32px] flex items-start justify-between opacity-70">
                    {calendarWeekdayLabels.map((label, idx) => (
                      <div key={`${label}-${idx}`} className="w-[30px] h-[32px] p-[4px] flex items-start justify-center">
                        <div className="w-[22px] h-[24px] flex items-center justify-center">
                          <span className="text-[10px] leading-[14px] font-normal text-[#333333]">{label.toLowerCase()}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="w-[208px] flex flex-col items-start">
                    {calendarMatrix.map((week, weekIndex) => (
                      <div key={weekIndex} className="w-[208px] h-[32px] flex items-start justify-between">
                        {week.map((day, dayIndex) => {
                          const isSelectedStart = isSameDay(day.date, parsedStartDate);
                          const isSelectedEnd = isSameDay(day.date, parsedEndDate);
                          const inRange = isWithinRange(day.date, parsedStartDate, parsedEndDate);
                          const isSelected = isSelectedStart || isSelectedEnd;
                          const cellOpacity = day.inMonth ? "" : "opacity-30";

                          const pillClass = isSelected
                            ? "bg-gradient-to-b from-[#DA3272] to-[#5438CE] text-white"
                            : inRange
                              ? "bg-[#F3E8FF] text-[#333333]"
                              : "bg-transparent text-[#333333]";

                          return (
                            <button
                              key={`${weekIndex}-${dayIndex}`}
                              type="button"
                              onClick={() => handleDaySelect(day.date)}
                              className={`w-[30px] h-[32px] p-[4px] flex items-start justify-center ${cellOpacity}`}
                            >
                              <span className={`${isSelected ? "w-[22px] h-[22px]" : "w-[22px] h-[24px]"} rounded-[99px] flex items-center justify-center ${pillClass}`}>
                                <span className={`text-[10px] leading-[14px] font-normal ${isSelected ? "text-white" : ""}`}>
                                  {day.label.padStart(2, "0")}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="px-[14px] py-[10px] flex items-center justify-start">
                  <button type="button" onClick={clearCalendarSelection} className="text-[12px] leading-[16px] text-[#5937D6]">
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-[12px] ml-auto">
            {(reviewFilter || startDate || endDate) && (
              <button type="button" onClick={clearFilters} className="text-[12px] leading-[155%] text-[#5937D6] hover:underline">
                Clear
              </button>
            )}
            {/* Pagination (top-right) */}
            {totalCount > 0 && (
              <Pagination
                variant="compact"
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={(next) => setPage(next)}
                onPageSizeChange={(nextSize) => {
                  setPageSize(nextSize);
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>

        {/* Table */}
        <div className="mt-[12px] w-full overflow-x-auto">
          <div className="min-w-[1100px] rounded-[4px] overflow-hidden">
            {/* Header */}
            <div className="h-[34px] border-b border-[#E0E2E5] bg-[#FDFEFF]">
              <div className="h-full pl-[24px] pr-[12px] grid grid-cols-[180px_190px_120px_140px_1fr] items-center text-[12px] leading-[155%] text-[#828282]">
                <div>Name</div>
                <div>Date</div>
                <div>Incidents</div>
                <div>Review Status</div>
                <div className="justify-self-end" />
              </div>
            </div>

            {/* Rows */}
            {filteredReports.length === 0 ? (
              <div className="h-[420px] flex flex-col items-center justify-center">
                <EmptyReportsIcon />
                <div className="mt-[18px] text-[12px] leading-[155%] text-[#969696]">
                  {emptyMessage}
                </div>
              </div>
            ) : (
              filteredReports.map((r) => {
                const isReviewed = r.stakeholder_confirmed !== null && r.stakeholder_confirmed !== undefined;
                const pill = reviewPill(isReviewed);

                const incidents =
                  r.incidents ??
                  r.incident_count ??
                  r.detections_count ??
                  r.detections?.length ??
                  0;

                return (
                  <div
                    key={r.id}
                    className="group h-[52px] bg-[#FDFEFF] border-b border-[#E0E2E5] transition-colors hover:bg-[#F3F5FE] hover:border-[#C9D8FC]"
                  >
                    <div className="h-full pl-[24px] pr-[12px] grid grid-cols-[180px_190px_120px_140px_1fr] items-center">
                      <div className="text-[14px] leading-[155%] text-[#000000]">Report #{r.id}</div>

                      <div className="text-[14px] leading-[155%] text-[#000000]">
                        {new Date(r.reported_at).toLocaleString("en-GB")}
                      </div>

                      <div className="text-[14px] leading-[155%] text-[#000000]">{incidents}</div>

                      <div>
                        <span
                          className={`inline-flex items-center justify-center ${pill.w} h-[23px] rounded-[182px] border text-[12px] leading-[155%]`}
                          style={{ background: pill.bg, borderColor: pill.border, color: pill.text }}
                        >
                          {pill.label}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="justify-self-end flex items-center gap-[12px]">
                        {(() => {
                          const actionBtnBase =
                            "w-[39px] h-[40px] rounded-[8px] flex items-center justify-center transition-colors duration-200";
                          const actionBtnIdle = "bg-transparent text-[#BBBABB]";
                          const actionBtnHover = "hover:bg-[#D4DAFE] hover:text-[#483ED1]";
                          const actionBtnDisabled = "disabled:opacity-50";

                          return (
                            <>
                              {/* Delete tile */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(r.id);
                                }}
                                disabled={deleteMutation.isPending}
                                className={[actionBtnBase, actionBtnIdle, actionBtnHover, actionBtnDisabled].join(" ")}
                                title="Delete report"
                              >
                                <TrashTileIcon />
                              </button>

                              {/* Review tile */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openReview(r);
                                }}
                                className={[actionBtnBase, actionBtnIdle, actionBtnHover].join(" ")}
                                title={isReviewed ? "View review" : "Review report"}
                              >
                                <ReviewTileIcon />
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Footer (outside the white card) */}
      <div className="w-full mt-[12px] flex items-center justify-between">
        <div className="text-[12px] leading-[155%] text-[#969696]">
          {totalCount === 0 ? "Showing 0 results" : `Showing ${showingFrom}-${showingTo} results`}
        </div>
      </div>


      {isReviewOpen && reviewReport &&
        createPortal(
          <div
            className="fixed inset-0 z-[90]"
            onClick={closeReview}
            role="dialog"
            aria-modal="true"
            aria-label={`Review report ${reviewReport.id}`}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-[#131313]/[0.69]" />

            {/* Center wrapper */}
            <div className="absolute inset-0 flex items-center justify-center p-[16px]">
              <div
                className="relative bg-white rounded-[8px] overflow-hidden"
                style={{
                  width: 980,
                  height: 650,
                  maxWidth: "calc(100vw - 32px)",
                  maxHeight: "calc(100vh - 32px)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="absolute left-[24px] top-[24px] right-[24px] h-[44px] flex items-center justify-between">
                  <div className="flex items-center gap-[16px]">
                    <button
                      type="button"
                      onClick={closeReview}
                      className="w-[40px] h-[40px] rounded-[8px] bg-[#C9D8FC] flex items-center justify-center"
                      aria-label="Close review"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M14.5 6.5L9.5 12L14.5 17.5"
                          stroke="#3C11BA"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <div className="font-['Open_Sans'] font-semibold text-[24px] leading-[155%] text-[#242424]">
                      Review — Report #{reviewReport.id}
                    </div>
                    <div className="flex items-center gap-[10px]">
                      <div className="font-['Open_Sans'] font-semibold text-[24px] leading-[155%] text-[#242424]">
                        Review — Report #{reviewReport.id}
                      </div>

                      {isReviewed ? (
                        <span
                          className={`inline-flex items-center justify-center h-[24px] px-[10px] rounded-[999px] text-[12px] font-semibold
                            ${isConfirmed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                        >
                          {isConfirmed ? "Confirmed" : "Not confirmed"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center h-[24px] px-[10px] rounded-[999px] bg-[#FFE2C6] text-[#885B0C] text-[12px] font-semibold">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Body layout: left info, right map */}
                <div className="absolute left-[24px] right-[24px] bottom-[90px]" style={{ top: 92 }}>
                  <div className="h-full grid grid-cols-[360px_1fr] gap-[16px]">
                    {/* Left: description + image */}
                    <div className="rounded-[8px] border border-[#E0E2E5] bg-white overflow-hidden">
                      <div className="p-[16px]">
                        <div className="text-[12px] text-[#828282] mb-[6px]">Description</div>
                        <div className="text-[14px] text-[#0B0B0B] whitespace-pre-wrap">
                          {reviewReport.description || "No description provided"}
                        </div>

                        <div className="mt-[16px] text-[12px] text-[#828282] mb-[6px]">Image</div>

                        {reviewReport?.image ? (
                          <div className="rounded-[8px] overflow-hidden border border-[#E0E2E5] bg-[#F4F7FA] relative">
                            {isSignedImgLoading && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                                <Spinner className="h-6 w-6" />
                              </div>
                            )}

                            <img
                              src={reviewImageUrl}
                              alt={`Report ${reviewReport.id}`}
                              className="w-full h-[240px] object-cover"
                              loading="lazy"
                              onError={() => {
                                if (!didRetryImg) {
                                  setDidRetryImg(true);
                                  refetchSignedImg();
                                }
                              }}
                            />

                            {isSignedImgError && (
                              <div className="mt-2 px-2 text-[12px] text-[#969696]">
                                Couldn’t load signed image URL (showing fallback if available).
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="h-[240px] rounded-[8px] border border-[#E0E2E5] bg-[#F4F7FA] flex items-center justify-center text-[12px] text-[#969696]">
                            No image attached
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: map */}
                    <div className="rounded-[8px] overflow-hidden border border-[#E0E2E5] bg-white">
                      <PointMap
                        key={reviewReport.id}
                        lat={reviewReport.location?.lat}
                        lng={reviewReport.location?.lng}
                        zoom={15}
                        height="100%"
                      />
                    </div>
                  </div>
                </div>

                {/* Footer buttons */}
                <div className="absolute left-[24px] right-[24px] bottom-[24px] flex items-center justify-between gap-[16px]">
                  <button
                    type="button"
                    onClick={() =>
                      confirmMutation.mutate(
                        { id: reviewReport.id, confirmed: false },
                        { onSuccess: closeReview }
                      )
                    }
                    disabled={confirmMutation.isLoading}
                    className="flex-1 h-[48px] rounded-[8px] bg-[#E7E3FA] text-[#2B1B66] text-[18px] font-semibold disabled:opacity-50"
                  >
                    Not confirm
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      confirmMutation.mutate(
                        { id: reviewReport.id, confirmed: true },
                        { onSuccess: closeReview }
                      )
                    }
                    disabled={confirmMutation.isLoading}
                    className="flex-1 h-[48px] rounded-[8px] bg-[#5638D1] text-white text-[18px] font-semibold disabled:opacity-50"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, reportId: null })}
        onConfirm={confirmDelete}
        title="Delete Report"
        message="Delete this incident report? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
